// src/components/layout/GlobalSearchBox.jsx
// Busqueda global del navbar: equipos y clientes (por ahora) a la vez.
// Dos formas de mostrarse segun el espacio disponible en el navbar:
//   - variante normal (sidebar/rail): pildora angosta con el icono
//     superpuesto que ensancha el input al enfocar (estilo del demo oficial
//     de MUI "Search AppBar").
//   - variante compact={true} (menu "Top", donde no hay lugar para una
//     pildora inline entre los items del menu): un solo icono, y al hacer
//     clic se abre un popover con el input DENTRO — mismo patron que un
//     boton de notificaciones/reclamos, para no romper el layout horizontal.
// Ambas comparten la misma logica de busqueda (debounce 400ms, mismo criterio
// que ya usa el resto de la app, ej. ClientePlataformaPanel) y el mismo
// listado de resultados agrupados por categoria.
import React, { useEffect, useRef, useState } from "react";
import { styled, alpha } from "@mui/material/styles";
import {
  Popper,
  Paper,
  ClickAwayListener,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  CircularProgress,
  Divider,
  InputBase,
  IconButton,
  Tooltip,
  Box,
  Chip,
} from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import SearchIcon from "@mui/icons-material/Search";
import HistoryIcon from "@mui/icons-material/History";
import CloseIcon from "@mui/icons-material/Close";
import axios from "../../config/axiosConfig";
import ClienteViewerDialog from "../ClienteViewerDialog";
import EquipoViewerDialog from "../EquipoViewerDialog";
import CentroEquipoDetalleDialog from "../../modules/requerimiento/components/CentroEquipoDetalleDialog";

const CENTRO_EQUIPO_TITLE = {
  radio: "Detalle del radio",
  chip: "Detalle del chip",
  bateria: "Detalle de la batería",
};

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: 0,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  width: "auto",
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 1.25),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "rgba(255,255,255,0.8)",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  "& .MuiInputBase-input": {
    padding: theme.spacing(0.6, 1, 0.6, 0),
    paddingLeft: `calc(1em + ${theme.spacing(3)})`,
    fontSize: "0.82rem",
    transition: theme.transitions.create("width"),
    width: "12ch",
    "&:focus": {
      width: "20ch",
    },
    "&::placeholder": {
      color: "rgba(255,255,255,0.65)",
      opacity: 1,
    },
  },
}));

// Sumar una categoria nueva es solo agregar la entrada aca — el backend ya
// maneja cada una en paralelo con su propio catch por categoria.
const CATEGORIAS = [
  { key: "equipos", label: "Equipos" },
  { key: "clientes", label: "Clientes" },
  { key: "radios", label: "Radios" },
  { key: "chips", label: "Chips" },
  { key: "baterias", label: "Baterías" },
];

// Color segun que tan reciente es la ultima conexion — mismo criterio
// semantico que un indicador "online/offline": verde si reporto hace menos
// de 1h, ambar si fue hoy, gris si es mas vieja.
const getUltimaConexion = (uc) => {
  if (!uc?.fecha) return null;
  const d = new Date(uc.fecha);
  if (Number.isNaN(d.getTime())) return null;
  const horas = (Date.now() - d.getTime()) / 36e5;
  const color = horas < 1 ? "#2e7d32" : horas < 24 ? "#ed6c02" : "#9e9e9e";
  const fecha = d.toLocaleString("es-BO", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  return {
    color,
    label: uc.plataforma ? `${fecha} · ${uc.plataforma}` : fecha,
  };
};

// Color segun el estado real del modem (equipo_modem.estado) — mismos
// valores que ya usa el resto de la app (Activo / Mantenimiento /
// Mantenimiento Traspaso / Inactivo / Vendido, etc.).
const getEstadoColor = (estado) => {
  const e = String(estado || "").toLowerCase();
  // "vendido" primero: "Vendido Activo" contiene la palabra "activo" tambien,
  // pero semanticamente ya no es un equipo activo en uso.
  if (e.includes("vendido") || e.includes("inactivo")) return "#9e9e9e";
  if (e.includes("mantenimiento")) return "#ed6c02";
  if (e.includes("activo")) return "#2e7d32";
  return "#607d8b";
};

// Mismos colores por tipo que EquiposChips.jsx (Requerimientos), para que el
// buscador global se vea consistente con esa tabla.
const TIPO_EQUIPO_COLOR = {
  radio: "primary",
  modem: "success",
  bateria: "warning",
  chip: "secondary",
  placa: "default",
  varios1: "info",
  varios2: "info",
};

// Ultimos resultados abiertos (no los textos tipeados): al elegirlos se abre
// directo el viewer, sin volver a buscar. Solo vive en este navegador.
const RECIENTES_KEY = "sort.busquedaGlobal.recientes";
const RECIENTES_MAX = 5;
const itemKey = (it) => `${it.tipo}-${it.id ?? it.valor}`;
const cargarRecientes = () => {
  try {
    const arr = JSON.parse(localStorage.getItem(RECIENTES_KEY) || "[]");
    return Array.isArray(arr) ? arr.slice(0, RECIENTES_MAX) : [];
  } catch {
    return [];
  }
};
const guardarRecientes = (arr) => {
  try {
    localStorage.setItem(RECIENTES_KEY, JSON.stringify(arr));
  } catch {
    /* sin storage: el historial simplemente no persiste */
  }
};

// Resalta la parte del texto que coincide con lo buscado.
function Resaltado({ texto, q }) {
  const t = String(texto ?? "");
  const term = (q || "").trim();
  if (term.length < 2) return t;
  const i = t.toLowerCase().indexOf(term.toLowerCase());
  if (i < 0) return t;
  return (
    <>
      {t.slice(0, i)}
      <Box component="mark" sx={{ bgcolor: "warning.light", color: "inherit", borderRadius: 0.5, px: 0.25 }}>
        {t.slice(i, i + term.length)}
      </Box>
      {t.slice(i + term.length)}
    </>
  );
}

function ListaRecientes({ recientes, activeIdx, onSelect, onRemove, onClear }) {
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", px: 1.5, pt: 1, pb: 0.25 }}>
        <Typography variant="caption" sx={{ flex: 1, fontWeight: 700, color: "text.secondary" }}>
          Recientes
        </Typography>
        <Typography
          variant="caption"
          role="button"
          onClick={onClear}
          sx={{ cursor: "pointer", color: "text.secondary", "&:hover": { textDecoration: "underline" } }}
        >
          Borrar todo
        </Typography>
      </Box>
      <List dense disablePadding>
        {recientes.map((item, idx) => (
          <ListItemButton
            key={itemKey(item)}
            selected={idx === activeIdx}
            ref={(el) => { if (el && idx === activeIdx) el.scrollIntoView({ block: "nearest" }); }}
            onClick={() => onSelect(item)}
          >
            <HistoryIcon sx={{ fontSize: 16, mr: 1, color: "text.disabled" }} />
            <ListItemText
              primary={item.titulo}
              secondary={item.subtitulo || null}
              primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 600, noWrap: true }}
              secondaryTypographyProps={{ fontSize: "0.72rem", noWrap: true }}
            />
            <IconButton
              size="small"
              aria-label="Quitar del historial"
              onClick={(e) => { e.stopPropagation(); onRemove(item); }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </ListItemButton>
        ))}
      </List>
    </Box>
  );
}

// Contenido del dropdown de resultados — compartido entre las dos variantes.
function ListaResultados({ cargando, resultados, onSelect, activeIdx = -1, q = "" }) {
  const total = resultados
    ? CATEGORIAS.reduce((acc, c) => acc + (resultados[c.key]?.length || 0), 0)
    : 0;

  if (cargando && !resultados) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }
  if (total === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2, fontStyle: "italic" }}>
        Sin resultados.
      </Typography>
    );
  }
  let flatIdx = -1;
  return CATEGORIAS.map((cat) => {
    const items = resultados?.[cat.key] || [];
    if (!items.length) return null;
    return (
      <Box key={cat.key}>
        <Typography
          variant="caption"
          sx={{ display: "block", px: 1.5, pt: 1, pb: 0.25, fontWeight: 700, color: "text.secondary" }}
        >
          {cat.label}
        </Typography>
        <List dense disablePadding>
          {items.map((item, idx) => {
            const ultimaConexion = getUltimaConexion(item.ultima_conexion);
            flatIdx += 1;
            const miIdx = flatIdx;
            const equiposPorTipo = item.tipo === "cliente" ? Object.entries(item.equipos || {}) : [];
            const tieneEquipos = equiposPorTipo.length > 0;
            return (
              <ListItemButton
                key={`${item.tipo}-${item.id}-${idx}`}
                selected={miIdx === activeIdx}
                ref={(el) => { if (el && miIdx === activeIdx) el.scrollIntoView({ block: "nearest" }); }}
                onClick={() => onSelect(item)}
              >
                <ListItemText
                  primary={<Resaltado texto={item.titulo} q={q} />}
                  secondary={
                    <>
                      {item.subtitulo ? <Resaltado texto={item.subtitulo} q={q} /> : null}
                      {(item.estado || ultimaConexion || tieneEquipos) && (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                          {item.estado && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={item.estado}
                              sx={{
                                height: 20,
                                fontSize: "0.66rem",
                                fontWeight: 700,
                                color: getEstadoColor(item.estado),
                                borderColor: alpha(getEstadoColor(item.estado), 0.4),
                                "& .MuiChip-label": { px: 0.75 },
                              }}
                            />
                          )}
                          {tieneEquipos &&
                            equiposPorTipo.map(([tipo, cantidad]) => (
                              <Chip
                                key={tipo}
                                size="small"
                                color={TIPO_EQUIPO_COLOR[tipo] || "default"}
                                label={`${tipo}: ${cantidad}`}
                                sx={{ height: 20, fontSize: "0.66rem", "& .MuiChip-label": { px: 0.75 } }}
                              />
                            ))}
                          {ultimaConexion && (
                            <Chip
                              size="small"
                              variant="outlined"
                              icon={
                                <FiberManualRecordIcon
                                  sx={{ fontSize: "9px !important", color: `${ultimaConexion.color} !important` }}
                                />
                              }
                              label={ultimaConexion.label}
                              sx={{
                                height: 20,
                                fontSize: "0.66rem",
                                borderColor: alpha(ultimaConexion.color, 0.4),
                                color: "text.secondary",
                                "& .MuiChip-label": { px: 0.75 },
                              }}
                            />
                          )}
                        </Box>
                      )}
                    </>
                  }
                  primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 600 }}
                  secondaryTypographyProps={{ fontSize: "0.72rem", component: "div" }}
                />
              </ListItemButton>
            );
          })}
        </List>
        <Divider />
      </Box>
    );
  });
}

// Logica compartida: input + debounce + llamada a la API + historial reciente
// + navegacion con teclado. Cada variante solo decide COMO se dispara/muestra
// (pildora inline vs icono + popover).
function useBusquedaGlobal() {
  const debounceRef = useRef(null);
  const [q, setQ] = useState("");
  const [cargando, setCargando] = useState(false);
  const [resultados, setResultados] = useState(null);
  const [recientes, setRecientes] = useState(cargarRecientes);
  const [activeIdx, setActiveIdx] = useState(-1);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    const term = q.trim();
    if (term.length < 2) {
      setResultados(null);
      setCargando(false);
      return undefined;
    }
    setCargando(true);
    debounceRef.current = setTimeout(() => {
      axios
        .get("/api/busqueda-global", { params: { q: term } })
        .then(({ data }) => setResultados(data))
        .catch(() => setResultados(null))
        .finally(() => setCargando(false));
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  const buscando = q.trim().length >= 2;
  // Lista plana en el mismo orden en que se pintan, para moverse con flechas.
  const visibles = buscando
    ? CATEGORIAS.flatMap((c) => resultados?.[c.key] || [])
    : recientes;

  useEffect(() => { setActiveIdx(-1); }, [q, resultados]);

  const agregarReciente = (item) => {
    setRecientes((prev) => {
      const next = [item, ...prev.filter((r) => itemKey(r) !== itemKey(item))].slice(0, RECIENTES_MAX);
      guardarRecientes(next);
      return next;
    });
  };
  const quitarReciente = (item) => {
    setRecientes((prev) => {
      const next = prev.filter((r) => itemKey(r) !== itemKey(item));
      guardarRecientes(next);
      return next;
    });
  };
  const borrarRecientes = () => { setRecientes([]); guardarRecientes([]); };

  // Flechas / Enter sobre la lista visible. Devuelve true si consumio la tecla.
  const manejarTecla = (e, onSelect) => {
    if (!visibles.length) return false;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % visibles.length);
      return true;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i <= 0 ? visibles.length - 1 : i - 1));
      return true;
    }
    if (e.key === "Enter") {
      // Enter sin seleccion explicita abre el primer resultado.
      const item = visibles[activeIdx >= 0 ? activeIdx : 0];
      if (item) { e.preventDefault(); onSelect(item); return true; }
    }
    return false;
  };

  return {
    q, setQ, cargando, resultados, setResultados, buscando,
    recientes, activeIdx, agregarReciente, quitarReciente, borrarRecientes, manejarTecla,
  };
}

function GlobalSearchBoxInline() {
  const anchorRef = useRef(null);
  const [abierto, setAbierto] = useState(false);
  const [clienteItem, setClienteItem] = useState(null);
  const [equipoItem, setEquipoItem] = useState(null);
  const [centroItem, setCentroItem] = useState(null);
  const inputRef = useRef(null);
  const {
    q, setQ, cargando, resultados, setResultados, buscando,
    recientes, activeIdx, agregarReciente, quitarReciente, borrarRecientes, manejarTecla,
  } = useBusquedaGlobal();

  // Ya no navega: abre el viewer correspondiente sin salir de la página.
  const handleSelect = (item) => {
    if (item.tipo === "equipo") setEquipoItem(item);
    else if (item.tipo === "cliente") setClienteItem(item);
    else if (["radio", "chip", "bateria"].includes(item.tipo)) setCentroItem(item);
    agregarReciente(item);
    setQ("");
    setResultados(null);
    setAbierto(false);
    inputRef.current?.blur();
  };

  // Atajo global: Ctrl+K (o "/" fuera de un campo de texto) enfoca el buscador.
  useEffect(() => {
    const onKey = (e) => {
      const enCampo = /^(INPUT|TEXTAREA|SELECT)$/.test(e.target?.tagName) || e.target?.isContentEditable;
      if (((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") || (e.key === "/" && !enCampo)) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <ClickAwayListener onClickAway={() => setAbierto(false)}>
      <Search ref={anchorRef}>
        <SearchIconWrapper>
          <SearchIcon fontSize="small" />
        </SearchIconWrapper>
        <StyledInputBase
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setAbierto(true)}
          onKeyDown={(e) => {
            if (e.key === "Escape") { setQ(""); setAbierto(false); e.target.blur(); return; }
            manejarTecla(e, handleSelect);
          }}
          placeholder="Buscar… (Ctrl+K)"
          inputProps={{ "aria-label": "buscar" }}
          inputRef={inputRef}
        />

        <Popper
          open={abierto && (buscando || recientes.length > 0)}
          anchorEl={anchorRef.current}
          placement="bottom-end"
          style={{ zIndex: 1500, width: 320 }}
        >
          <Paper elevation={4} sx={{ mt: 0.5, maxHeight: 420, overflowY: "auto", color: "text.primary" }}>
            {buscando ? (
              <ListaResultados cargando={cargando} resultados={resultados} onSelect={handleSelect} activeIdx={activeIdx} q={q} />
            ) : (
              <ListaRecientes
                recientes={recientes}
                activeIdx={activeIdx}
                onSelect={handleSelect}
                onRemove={quitarReciente}
                onClear={borrarRecientes}
              />
            )}
          </Paper>
        </Popper>

        <ClienteViewerDialog open={!!clienteItem} onClose={() => setClienteItem(null)} cliente={clienteItem} />
        <EquipoViewerDialog open={!!equipoItem} onClose={() => setEquipoItem(null)} equipo={equipoItem} />
        <CentroEquipoDetalleDialog
          open={!!centroItem}
          onClose={() => setCentroItem(null)}
          tipo={centroItem?.tipo}
          identificador={centroItem?.valor}
          title={CENTRO_EQUIPO_TITLE[centroItem?.tipo] || "Detalle del equipo"}
        />
      </Search>
    </ClickAwayListener>
  );
}

// Variante compacta (menu "Top"): un icono nada mas, igual que los botones de
// notificaciones vecinos — al hacer clic abre un popover con el input adentro
// en vez de ensanchar algo entre los items del menu (ahi no hay lugar).
function GlobalSearchBoxCompact() {
  const anchorRef = useRef(null);
  const inputRef = useRef(null);
  const [abierto, setAbierto] = useState(false);
  const [clienteItem, setClienteItem] = useState(null);
  const [equipoItem, setEquipoItem] = useState(null);
  const [centroItem, setCentroItem] = useState(null);
  const {
    q, setQ, cargando, resultados, setResultados, buscando,
    recientes, activeIdx, agregarReciente, quitarReciente, borrarRecientes, manejarTecla,
  } = useBusquedaGlobal();

  const handleAbrir = () => {
    setAbierto(true);
    setTimeout(() => inputRef.current?.focus(), 60);
  };

  const handleCerrar = () => {
    setAbierto(false);
    setQ("");
    setResultados(null);
  };

  // Ya no navega: abre el viewer correspondiente sin salir de la página.
  const handleSelect = (item) => {
    if (item.tipo === "equipo") setEquipoItem(item);
    else if (item.tipo === "cliente") setClienteItem(item);
    else if (["radio", "chip", "bateria"].includes(item.tipo)) setCentroItem(item);
    agregarReciente(item);
    handleCerrar();
  };

  return (
    <ClickAwayListener onClickAway={handleCerrar}>
      <Box sx={{ display: "inline-flex" }}>
        <Tooltip title="Buscar">
          <IconButton color="inherit" size="small" onClick={handleAbrir} ref={anchorRef}>
            <SearchIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Popper
          open={abierto}
          anchorEl={anchorRef.current}
          placement="bottom-end"
          style={{ zIndex: 1500, width: 320 }}
        >
          <Paper elevation={4} sx={{ mt: 0.5, maxHeight: 460, overflow: "hidden", color: "text.primary" }}>
            <Box sx={{ p: 1, borderBottom: "1px solid", borderColor: "divider" }}>
              <InputBase
                inputRef={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") { handleCerrar(); return; }
                  manejarTecla(e, handleSelect);
                }}
                placeholder="Buscar equipos o clientes…"
                fullWidth
                sx={{ fontSize: "0.85rem", px: 0.5 }}
                startAdornment={<SearchIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />}
              />
            </Box>
            <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
              {buscando ? (
                <ListaResultados cargando={cargando} resultados={resultados} onSelect={handleSelect} activeIdx={activeIdx} q={q} />
              ) : recientes.length > 0 ? (
                <ListaRecientes
                  recientes={recientes}
                  activeIdx={activeIdx}
                  onSelect={handleSelect}
                  onRemove={quitarReciente}
                  onClear={borrarRecientes}
                />
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, fontStyle: "italic" }}>
                  Escribe al menos 2 letras…
                </Typography>
              )}
            </Box>
          </Paper>
        </Popper>

        <ClienteViewerDialog open={!!clienteItem} onClose={() => setClienteItem(null)} cliente={clienteItem} />
        <EquipoViewerDialog open={!!equipoItem} onClose={() => setEquipoItem(null)} equipo={equipoItem} />
        <CentroEquipoDetalleDialog
          open={!!centroItem}
          onClose={() => setCentroItem(null)}
          tipo={centroItem?.tipo}
          identificador={centroItem?.valor}
          title={CENTRO_EQUIPO_TITLE[centroItem?.tipo] || "Detalle del equipo"}
        />
      </Box>
    </ClickAwayListener>
  );
}

export default function GlobalSearchBox({ compact = false }) {
  return compact ? <GlobalSearchBoxCompact /> : <GlobalSearchBoxInline />;
}
